import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

export const ContactForm = () => {
  const { colors, theme } = useTheme();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    console.log(values);
    toast.success("Message sent successfully!");
    form.reset();
  };

  return (
    <div 
      className="backdrop-blur-md border rounded-lg p-6 md:p-8 transition-colors duration-300 shadow-2xl"
      style={{ 
        backgroundColor: theme === 'light' ? `${colors.card}95` : `${colors.card}95`,
        borderColor: `${colors.primary}40`,
        boxShadow: `0 12px 40px ${colors.primary}15, 0 8px 25px ${colors.primary}10, inset 0 1px 0 ${colors.primary}20`,
        backdropFilter: 'blur(20px) saturate(1.8)',
      }}
    >
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-3xl md:text-4xl font-bold mb-4 text-center pb-2"
        style={{
          background: colors.gradient,
          backgroundSize: "200% auto",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: `0 0 20px ${colors.primary}30`
        }}
      >
        Send us a Message
      </motion.h3>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel style={{ color: colors.foreground }} className="font-medium">Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Your name" 
                    {...field} 
                    className="border focus:ring-2 focus:ring-offset-0 transition-all duration-200"
                    style={{
                      backgroundColor: colors.input || colors.card,
                      borderColor: colors.inputBorder || colors.border,
                      color: colors.foreground,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.inputBorder || colors.border;
                    }}
                  />
                </FormControl>
                <FormMessage style={{ color: colors.error }} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel style={{ color: colors.foreground }} className="font-medium">Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="Your email" 
                    {...field} 
                    className="border focus:ring-2 focus:ring-offset-0 transition-all duration-200"
                    style={{
                      backgroundColor: colors.input || colors.card,
                      borderColor: colors.inputBorder || colors.border,
                      color: colors.foreground,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.inputBorder || colors.border;
                    }}
                  />
                </FormControl>
                <FormMessage style={{ color: colors.error }} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel style={{ color: colors.foreground }} className="font-medium">Message</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Your message" 
                    {...field} 
                    className="border focus:ring-2 focus:ring-offset-0 min-h-[120px] transition-all duration-200"
                    style={{
                      backgroundColor: colors.input || colors.card,
                      borderColor: colors.inputBorder || colors.border,
                      color: colors.foreground,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.inputBorder || colors.border;
                    }}
                  />
                </FormControl>
                <FormMessage style={{ color: colors.error }} />
              </FormItem>
            )}
          />
          <Button 
            type="submit" 
            className="w-full text-white font-semibold transition-all duration-300 py-3 hover:scale-105"
            style={{
              background: colors.gradient,
            }}
          >
            Send Message
          </Button>
        </form>
      </Form>
    </div>
  );
};